import React from 'react';

import Field from '@atlaskit/form/field';
import { ErrorMessage, HelperMessage } from '@atlaskit/form/messages';
import Suspense from '@atlaskit/react-ufo/suspense';
import Select from '@atlaskit/select/select';
import type { OptionType, ValueType } from '@atlaskit/select/types';
import { defineMessage, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { ErrorBoundary } from '@townsquare/monitoring/error-boundary';

import { TeamTypeLabel } from '../TeamTypeLabel';
import type { StringOption } from '../types';

import type {
	TeamTypeScopeSelectQuery,
	TeamTypeState,
} from './__generated__/TeamTypeScopeSelectQuery.graphql';

const teamTypeScopeLabelMessage = defineMessage({
	id: 'townsquare.teams-settings.custom-field-form.team-type-scope-label',
	defaultMessage: 'Team type',
	description:
		'Label shown above the optional team type dropdown that scopes a custom field to a single team type.',
});

const teamTypeScopePlaceholderMessage = defineMessage({
	id: 'townsquare.teams-settings.custom-field-form.team-type-scope-placeholder',
	defaultMessage: 'Select a team type',
	description:
		'Placeholder text for the optional team type dropdown in the custom field creation form.',
});

const teamTypeScopeHelperMessage = defineMessage({
	id: 'townsquare.teams-settings.custom-field-form.team-type-scope-helper-message',
	defaultMessage: 'Leave empty to apply this field to all teams.',
	description:
		'Helper text beneath the team type dropdown, explaining that an empty selection applies the field to all teams.',
});

const teamTypeScopeErrorMessage = defineMessage({
	id: 'townsquare.teams-settings.custom-field-form.team-type-scope-error-message',
	defaultMessage:
		"We couldn't load team types. You can still create this field without scoping it to a team type.",
	description:
		'Error message shown when the list of team types fails to load, explaining the team type scope is unavailable.',
});

type TeamTypeScopeSelectProps = {
	scopeId?: string;
	initialValue?: ValueType<StringOption> | null;
	mode?: 'create' | 'edit';
};

const formatTeamTypeOptionLabel = (option: OptionType) => {
	const { label, verified } = option as StringOption;
	return <TeamTypeLabel name={label} verified={verified} />;
};

type TeamTypeScopeSelectFieldProps = {
	initialValue?: ValueType<StringOption> | null;
	options?: StringOption[];
	isDisabled?: boolean;
	isLoading?: boolean;
	isClearable?: boolean;
	errorMessage?: string;
};

const TeamTypeScopeSelectField = ({
	initialValue,
	options,
	isDisabled,
	isLoading,
	isClearable,
	errorMessage,
}: TeamTypeScopeSelectFieldProps) => {
	const intl = useIntl();

	return (
		<Field<ValueType<OptionType>>
			name="teamTypeScope"
			label={intl.formatMessage(teamTypeScopeLabelMessage)}
			defaultValue={initialValue ?? null}
		>
			{({ fieldProps }) => (
				<React.Fragment>
					<Select
						{...fieldProps}
						inputId="team-type-scope-select"
						options={options}
						isDisabled={isDisabled}
						isLoading={isLoading}
						isClearable={isClearable}
						formatOptionLabel={formatTeamTypeOptionLabel}
						placeholder={intl.formatMessage(teamTypeScopePlaceholderMessage)}
					/>
					{errorMessage ? (
						/* eslint-disable-next-line @atlaskit/design-system/use-field-message-wrapper -- TODO: migrate to MessageWrapper */
						<ErrorMessage>{errorMessage}</ErrorMessage>
					) : (
						/* eslint-disable-next-line @atlaskit/design-system/use-field-message-wrapper -- TODO: migrate to MessageWrapper */
						<HelperMessage>{intl.formatMessage(teamTypeScopeHelperMessage)}</HelperMessage>
					)}
				</React.Fragment>
			)}
		</Field>
	);
};

const TeamTypeScopeSelectInternal = ({
	scopeId,
	initialValue,
}: {
	scopeId: string;
	initialValue?: ValueType<StringOption> | null;
}) => {
	const data = useLazyLoadQuery<TeamTypeScopeSelectQuery>(
		graphql`
			query TeamTypeScopeSelectQuery($scopeId: ID!) {
				team {
					typesWithinScope(scopeId: $scopeId, first: 50) @optIn(to: "Team-types") {
						edges {
							node {
								id
								name
								state
								verified
							}
						}
					}
				}
			}
		`,
		{ scopeId },
	);

	const teamTypeOptions: StringOption[] = (data?.team?.typesWithinScope?.edges ?? [])
		.filter(
			(
				edge,
			): edge is {
				readonly node: {
					readonly id: string;
					readonly name: string;
					readonly state: TeamTypeState;
					readonly verified: boolean | null;
				};
			} => edge?.node != null && edge.node.state === 'ACTIVE',
		)
		.map((edge) => ({
			label: edge.node.name,
			value: edge.node.id,
			verified: edge.node.verified ?? false,
		}));

	return (
		<TeamTypeScopeSelectField initialValue={initialValue} options={teamTypeOptions} isClearable />
	);
};

export const TeamTypeScopeSelect = ({
	scopeId,
	initialValue,
	mode = 'create',
}: TeamTypeScopeSelectProps): React.JSX.Element | null => {
	const intl = useIntl();

	if (mode === 'edit') {
		return <TeamTypeScopeSelectField initialValue={initialValue} isDisabled />;
	}

	if (!scopeId) {
		return null;
	}

	return (
		<ErrorBoundary
			fallback={
				<TeamTypeScopeSelectField
					isDisabled
					errorMessage={intl.formatMessage(teamTypeScopeErrorMessage)}
				/>
			}
		>
			<Suspense
				interactionName="team-type-scope-select"
				fallback={<TeamTypeScopeSelectField isDisabled isLoading />}
			>
				<TeamTypeScopeSelectInternal scopeId={scopeId} initialValue={initialValue} />
			</Suspense>
		</ErrorBoundary>
	);
};
